import { createContext, useCallback, useContext, useState } from "react";

export const SessionContextPanelType = {
  AGENT: "agent",
  DATASET: "dataset",
  APPS: "apps",
  // The full session ingestion list (opened from the single Apps > Ingestions
  // entry); a row in it opens INGEST for one job.
  INGEST_LIST: "ingest-list",
  INGEST: "ingest",
  // The full session eval-run list (opened from the single Apps > Eval runs
  // entry); a row in it opens EVAL_RUN for one run.
  EVAL_RUN_LIST: "eval-run-list",
  EVAL_RUN: "eval-run",
  // The full session DAG-run list (opened from the single Apps >
  // Pipeline runs entry); a row in it opens DAG_RUN for one run.
  DAG_RUN_LIST: "dag-run-list",
  DAG_RUN: "dag-run",
  // The current task plan (opened from the single Apps > Plan entry).
  PLAN: "plan",
} as const;
export type SessionContextPanelType =
  (typeof SessionContextPanelType)[keyof typeof SessionContextPanelType];

const DEFAULT_VIEW = "overview";

interface SessionContextPanelState {
  type: SessionContextPanelType;
  id: string;
  // Which sub-view is showing within the entity's detail (e.g. "overview",
  // "skills"). Always defined once a panel is open — defaults to "overview".
  view: string;
}

interface UseSessionContextPanelResult {
  // null means the sidebar shows its table-of-contents (home) view.
  panel: SessionContextPanelState | null;
  // `id` is optional — list-style panels (e.g. the ingestion list) carry none.
  openPanel: (type: SessionContextPanelType, id?: string) => void;
  setView: (view: string) => void;
  goHome: () => void;
  // Un-collapses the sidebar. A no-op where no layout owns a collapsible sidebar.
  expandSidebar: () => void;
}

const NOOP = () => {};

// Shared so the sidebar (mounted alongside `<Outlet>` in ProjectLayout) and
// the MCP-apps sync hook (mounted deep inside the routed Chat) can both
// drive which entity/app the sidebar shows, despite neither being an
// ancestor of the other.
const SessionContextPanelContext = createContext<UseSessionContextPanelResult | null>(null);

export const SessionContextPanelProvider = ({
  conversationId,
  onExpandSidebar = NOOP,
  children,
}: {
  // The active conversation, so the panel resets when the user switches
  // sessions. Optional: non-conversation mounts (and unit tests) pass none.
  conversationId?: string;
  // Must be stable: `useMcpAppsPaneSync`'s live-subscription effect depends on it.
  onExpandSidebar?: () => void;
  children: React.ReactNode;
}) => {
  const [panel, setPanel] = useState<SessionContextPanelState | null>(null);

  // Reset to home (closed) whenever the active conversation changes, so a panel
  // opened in one session — e.g. the Plan panel auto-opened on `write_plan` —
  // does not linger over the next. Done while rendering rather than with a
  // `key` (which would remount the whole Session* provider stack and the routed
  // <Outlet>, whose ConversationRoute is deliberately kept mounted across
  // switches) or an effect (disallowed for resetting state).
  const [prevConversationId, setPrevConversationId] = useState(conversationId);
  if (conversationId !== prevConversationId) {
    setPrevConversationId(conversationId);
    setPanel(null);
  }

  // Stable across renders (functional updates only) so effects that depend on
  // these — e.g. `useMcpAppsPaneSync`'s live-subscription effect — don't
  // re-run, and reset their own state, every time the panel changes.
  const openPanel = useCallback((type: SessionContextPanelType, id = "") => {
    setPanel({ type, id, view: DEFAULT_VIEW });
  }, []);

  const setView = useCallback((view: string) => {
    setPanel((prev) => (prev ? { ...prev, view } : prev));
  }, []);

  const goHome = useCallback(() => {
    setPanel(null);
  }, []);

  return (
    <SessionContextPanelContext.Provider
      value={{ panel, openPanel, setView, goHome, expandSidebar: onExpandSidebar }}
    >
      {children}
    </SessionContextPanelContext.Provider>
  );
};

// Like `useSessionContextPanel`, but returns null outside a provider (e.g. the
// read-only conversation views, which have no sidebar).
export function useSessionContextPanelOptional(): UseSessionContextPanelResult | null {
  return useContext(SessionContextPanelContext);
}

// Backs the session context sidebar's TOC/detail state and which
// dataset/agent/app + sub-view it's showing.
export function useSessionContextPanel(): UseSessionContextPanelResult {
  const ctx = useContext(SessionContextPanelContext);
  if (!ctx) {
    throw new Error("useSessionContextPanel must be used within a SessionContextPanelProvider");
  }
  return ctx;
}
